import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Avatar, Button, Card, CardEmpty, EmptyState, Icon, IconButton, Row, Rows, Spinner } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { ApiError } from "../../app/hooks/apiError.js";
import {
  useTeams,
  useDisconnectOrg,
  useGenerateLinkCode,
  useRedeemLinkCode,
  type TeamView,
} from "../../app/hooks/useTeams.js";
import { orgTypeLabel, orgInitials } from "../../app/hooks/useOrg.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import { LinkCodeModal } from "../shared/LinkCodeModal.js";
import styles from "./TeamsCard.module.scss";

/**
 * The Teams card — rendered inside ContactsScreen. Lists the band's linked partner orgs
 * (Label / Management / Booking) from `GET /bands/{bandId}/teams`, gated to `band:manage` for the write
 * affordances (link / disconnect). Linking is the one-time-CODE handshake (PO,
 * 2026-08-20 — the email-lookup invite left this surface): generate a code for your team, or redeem
 * one they generated; the link goes live instantly. Born-blank empty, loading, and error states.
 * The row's TYPE pill is the org's own kind (a management account is management — derived, never
 * band-declared; PO 2026-08-20, the change-type menu is retired). The one per-team action is Remove
 * team (disconnect, destructive double-confirm — "Current sharing stops. Past data stays.").
 */
export function TeamsCard() {
  const list = useTeams();
  const canManage = useCapability("band:manage");
  const [inviting, setInviting] = useState(false);

  const items = list.data?.items ?? [];

  return (
    <Card
      id="teams"
      title="Team"
      subtitle="Managers, Label, & Booking Agent"
      subtitleVariant="tight"
      flush
      /* The list renders for every seat; only the button is gated. */
      actions={
        canManage && (
          <Button size="sm" onClick={() => setInviting(true)}>
            <Icon icon={Plus} size={16} /> Link a team
          </Button>
        )
      }
    >

      <div className={styles.body}>
        {list.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={20} label="Loading teams…" />
          </div>
        ) : list.isError ? (
          <TeamsError error={list.error} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          <CardEmpty>
            {canManage ? (
              <>
                No teams connected yet, link a partner with a one-time code.
              </>
            ) : (
              <>No teams connected yet.</>
            )}
          </CardEmpty>
        ) : (
          <Rows>
            {items.map((team) => (
              <TeamRow key={team.orgId ?? team.linkId} team={team} canManage={canManage} />
            ))}
          </Rows>
        )}
      </div>

      {inviting && <LinkTeamModal onClose={() => setInviting(false)} />}
    </Card>
  );
}

/** The band side of the code handshake — wires the band-scoped mutations into the shared modal. */
function LinkTeamModal({ onClose }: { onClose: () => void }) {
  const generate = useGenerateLinkCode();
  const redeem = useRedeemLinkCode();
  return (
    <LinkCodeModal
      generateHint="Generate a one-time code, then send it to your label, manager, or booking agent. They redeem it from their roster and the link goes live instantly."
      redeemHint="Enter the code your label, manager, or booking agent generated for you."
      redeemedNote="They're on your team now — the link appears in Your Teams."
      onGenerate={() => generate.mutateAsync(undefined)}
      onRedeem={(code) => redeem.mutateAsync(code)}
      generating={generate.isPending}
      redeeming={redeem.isPending}
      onClose={onClose}
    />
  );
}

function TeamsError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) {
    return <EmptyState title="Teams unavailable" description="Your role on this band doesn't include Teams." />;
  }
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return (
    <EmptyState title="Couldn't load teams" description={message} action={<Button onClick={onRetry}>Retry</Button>} />
  );
}

function TeamRow({ team, canManage }: { team: TeamView; canManage: boolean }) {
  const disconnect = useDisconnectOrg();
  const [confirming, setConfirming] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const name = team.orgName || "Unnamed organization";
  // The org's LOGO, from the global avatar store, falling back to the server-resolved public URL.
  //
  // Both, because they fail in opposite places. `logoUrl` comes from IdentityMediaResolver, which brackets
  // the foreign tenant to read its media and hands back a CDN URL — and returns NULL on any lane with no
  // public media base (every local box, and dev), so this row has rendered initials there since it shipped.
  // The avatar route has no such hole but only answers for a logo saved SINCE the global store landed, since
  // the crop is derived on save and nothing backfilled it. Whichever exists wins; a logo that has neither
  // renders the initials the row was always designed to fall back to.
  const logoUrl = useAvatarUrl("org", team.orgId, team.logoMediaId) ?? team.logoUrl;
  const pending = team.status != null && team.status !== "linked" && team.status !== "active";

  function onConfirmDisconnect() {
    if (!team.orgId) return;
    setDisconnectError(null);
    disconnect.mutate(team.orgId, {
      onSuccess: () => setConfirming(false),
      onError: (err) => setDisconnectError(err.message),
    });
  }

  return (
    <Row
      avatar={<Avatar initials={orgInitials(name)} src={logoUrl} shape="rounded" />}
      name={name}
      sub={
        <span className={styles.rowMeta}>
          <span className={styles.pill}>{orgTypeLabel(team.linkType)}</span>
          {pending && <span className={styles.pillMuted}>{team.status}</span>}
        </span>
      }
      trailing={
        <>
          {canManage && (
            <IconButton
              icon={Trash2}
              label={`Remove ${name}`}
              tone="danger"
              size="sm"
              onClick={() => setConfirming(true)}
            />
          )}
          {confirming && (
            <ConfirmDialog
              title={`Remove ${name}?`}
              description="Current sharing stops and every assignment drops. Past data stays."
              confirmLabel="Remove team"
              error={disconnectError}
              loading={disconnect.isPending}
              onConfirm={onConfirmDisconnect}
              onCancel={() => setConfirming(false)}
            />
          )}
        </>
      }
    />
  );
}
